// frontend/src/utils/api.js

import { getToken } from "./auth";

const RAW_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
const NORMALIZED_BASE = RAW_BASE.replace(/\/+$/, "");
const API_BASE_URL = NORMALIZED_BASE.endsWith("/api")
  ? NORMALIZED_BASE
  : `${NORMALIZED_BASE}/api`;

export function getApiBaseUrl() {
  return API_BASE_URL;
}

export function getAssetUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("data:")) return path;
  // If it's a relative path like /uploads/..., append to domain root
  const root = API_BASE_URL.replace(/\/api$/, "");
  return `${root}${path.startsWith("/") ? "" : "/"}${path}`;
}

if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.debug("API base URL", API_BASE_URL);
}

export async function apiRequest(path, options = {}) {
  // Prefer the app's persisted auth storage
  const token = getToken();

  const isFormData =
    options?.isFormData === true || (typeof FormData !== "undefined" && options?.body instanceof FormData);

  const headers = {
    ...(options.headers || {})
  };

  // Only set JSON content-type for non-FormData payloads.
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
      credentials: "omit"
    });
  } catch (err) {
    const error = {
      message: "Unable to connect to server. Please check your internet connection or try again later.",
      error: "Connection Failed",
      isNetworkError: true
    };
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("API request failed", {
        baseUrl: API_BASE_URL,
        path,
        message: err?.message
      });
    }
    throw error;
  }

  if (!response.ok) {
    let error;
    try {
      error = await response.json();
    } catch {
      error = { message: response.statusText };
    }

    // Normalize error shape for consistent handling.
    if (error && typeof error === "object") {
      error.status = response.status;
    } else {
      error = {
        status: response.status,
        message: response.statusText
      };
    }

    // ── VPN_001: Show non-dismissible full-screen modal ──────────────────────
    if (error.code === "VPN_001") {
      showVpnModal(() => apiRequest(path, options));
      throw error;
    }

    // ── Session Breaking Auth Failures ───────────────────────────────────────
    const SESSION_BREAKING_CODES = [
      "AUTH_MISSING_TOKEN",
      "AUTH_INVALID_TOKEN",
      "AUTH_ACCOUNT_DISABLED",
      "AUTH_USER_DELETED",
      // Deleting the account logs out every phone that was signed in before (Oct 2026).
      "AUTH_DELETION_LOGGED_OUT"
    ];
    
    error.isSessionExpired = response.status === 401 && SESSION_BREAKING_CODES.includes(error.code);

    // Signed in again while the account is being deleted: the only page left is the one
    // that offers to keep it.
    if (response.status === 403 && error.code === "ACCOUNT_DELETION_PENDING"
        && typeof window !== "undefined" && window.location.pathname !== "/account/deletion-pending") {
      window.location.replace("/account/deletion-pending");
    }

    if (error.isSessionExpired) {
      import("../auth/AuthService").then((module) => {
        const AuthService = module.default;
        AuthService.logout();
      });

      // Avoid leaking backend error strings like "Missing auth" into the UI.
      error.error = "Your session has expired. Please log in again.";
      error.message = error.error;
    }

    throw error;
  }

  return response.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// VPN Modal — vanilla DOM, inline CSS only, non-dismissible
// ─────────────────────────────────────────────────────────────────────────────

const VPN_MODAL_ID = "sramika-vpn-modal";

function showVpnModal(retryFn) {
  // Only inject once
  if (document.getElementById(VPN_MODAL_ID)) return;

  // ── Overlay ────────────────────────────────────────────────────────────────
  const overlay = document.createElement("div");
  overlay.id = VPN_MODAL_ID;
  overlay.style.cssText = [
    "position: fixed",
    "inset: 0",
    "z-index: 999999",
    "background: rgba(15, 23, 42, 0.92)",
    "backdrop-filter: blur(6px)",
    "-webkit-backdrop-filter: blur(6px)",
    "display: flex",
    "align-items: center",
    "justify-content: center",
    "padding: 24px",
    "font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  ].join(";");

  // Prevent backdrop clicks from dismissing
  overlay.addEventListener("click", (e) => e.stopPropagation());

  // ── Card ───────────────────────────────────────────────────────────────────
  const card = document.createElement("div");
  card.style.cssText = [
    "background: #ffffff",
    "border-radius: 24px",
    "padding: 40px 32px 36px",
    "max-width: 360px",
    "width: 100%",
    "text-align: center",
    "box-shadow: 0 32px 80px rgba(0,0,0,0.40)",
    "animation: sramikaVpnSlide 0.3s ease-out",
  ].join(";");

  // ── Inject keyframe animation ──────────────────────────────────────────────
  if (!document.getElementById("sramika-vpn-keyframes")) {
    const style = document.createElement("style");
    style.id = "sramika-vpn-keyframes";
    style.textContent = `
      @keyframes sramikaVpnSlide {
        from { opacity: 0; transform: translateY(24px) scale(0.97); }
        to   { opacity: 1; transform: translateY(0)    scale(1);    }
      }
      @keyframes sramikaVpnPulse {
        0%,100% { transform: scale(1); }
        50%      { transform: scale(1.08); }
      }
    `;
    document.head.appendChild(style);
  }

  // ── Icon ───────────────────────────────────────────────────────────────────
  const iconWrap = document.createElement("div");
  iconWrap.style.cssText = [
    "width: 72px",
    "height: 72px",
    "border-radius: 50%",
    "background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
    "display: flex",
    "align-items: center",
    "justify-content: center",
    "margin: 0 auto 24px",
    "animation: sramikaVpnPulse 2.4s ease-in-out infinite",
  ].join(";");
  iconWrap.innerHTML = `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`;

  // ── Title ──────────────────────────────────────────────────────────────────
  const title = document.createElement("h2");
  title.style.cssText = [
    "margin: 0 0 10px",
    "font-size: 20px",
    "font-weight: 800",
    "color: #1A1A2E",
    "letter-spacing: -0.3px",
  ].join(";");
  title.textContent = "VPN Detected";

  // ── Body text ──────────────────────────────────────────────────────────────
  const body = document.createElement("p");
  body.style.cssText = [
    "margin: 0 0 28px",
    "font-size: 14px",
    "line-height: 1.65",
    "color: #4B5563",
    "font-weight: 500",
  ].join(";");
  body.textContent =
    "Sramika is not available with VPN or proxy connections. Please disable your VPN and try again to continue booking.";

  // ── Try Again button ───────────────────────────────────────────────────────
  const btn = document.createElement("button");
  btn.style.cssText = [
    "width: 100%",
    "padding: 14px 0",
    "border-radius: 50px",
    "border: none",
    "background: linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)",
    "color: #ffffff",
    "font-size: 14px",
    "font-weight: 700",
    "cursor: pointer",
    "letter-spacing: 0.2px",
    "transition: opacity 0.2s",
    "display: flex",
    "align-items: center",
    "justify-content: center",
    "gap: 8px",
  ].join(";");
  btn.textContent = "Try Again";

  btn.addEventListener("mouseenter", () => { btn.style.opacity = "0.88"; });
  btn.addEventListener("mouseleave", () => { btn.style.opacity = "1"; });

  btn.addEventListener("click", async () => {
    btn.disabled = true;
    btn.textContent = "Checking\u2026";
    try {
      await retryFn();
      // Only remove modal if the retry succeeded (no VPN_001 thrown)
      removeVpnModal();
    } catch (err) {
      // If still blocked (VPN_001), modal stays. Any other error: dismiss.
      if (err?.code !== "VPN_001") {
        removeVpnModal();
      }
      btn.disabled = false;
      btn.textContent = "Try Again";
    }
  });

  // ── Support text ───────────────────────────────────────────────────────────
  const support = document.createElement("p");
  support.style.cssText = [
    "margin: 16px 0 0",
    "font-size: 11px",
    "color: #9CA3AF",
    "font-weight: 500",
  ].join(";");
  support.innerHTML = 'Need help? <a href="mailto:support@sramika.com" style="color:#4F46E5;text-decoration:none;font-weight:600;">Contact support</a>';

  // ── Assemble ───────────────────────────────────────────────────────────────
  card.appendChild(iconWrap);
  card.appendChild(title);
  card.appendChild(body);
  card.appendChild(btn);
  card.appendChild(support);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}

function removeVpnModal() {
  const el = document.getElementById(VPN_MODAL_ID);
  if (el) el.remove();
}
